import {useElements, useStripe} from '@stripe/react-stripe-js'
import pRetry from 'p-retry'
import {Breadcrumb, Breadcrumbs} from 'react-aria-components'

import {useIsAuthenticatedValue} from '@/auth/hooks/useIsAuthenticated'
import Loading from '@/components/Loading/Loading'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import SkipLink from '@/components/SkipLink'
import Navbar from '@/layout/Navbar'
import {BookingStatus, parseBookingType, parseTravelClass} from '@/lib/flight/flightModel'
import {bookFlight, confirmBooking, getBooking} from '@/lib/flight/flightService'
import {assistanceInformationAtom, passengersAndContactAtom} from '@/lib/flight/store'
import HeadTags from '@/lib/head/HeadTags'
import {useLocale} from '@/lib/locale/locale'
import ContactInfo, {type ContactInfoControl} from '@/lib/payment/components/ContactInfo'
import PaymentMethod from '@/lib/payment/components/PaymentMethod'
import {CLIENT_SECRET_FOR_0_AMOUNT_PAYMENT} from '@/lib/payment/constants'
import PaymentProvider from '@/lib/payment/PaymentProvider'
import {contactAtom, getRoundUp} from '@/lib/payment/store'
import {RESOLVED_ERRORS, StripeElement} from '@/lib/payment/stripeErrors'
import {FlightCheckoutRoute, FlightDetailsRoute, FlightRoute} from '@/routeRegistry'
import {FLIGHT_ROUTE_DEFAULT_SEARCH} from '@/routes/_flight/flights/index'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import UnexpectedError from '@/utils/api/UnexpectedError'
import toastErrorMessage from '@/utils/errors/toastErrorMessage'
import RACLink from '@/utils/router/RACLink'
import useSearch from '@/utils/router/useSearch'
import tryCatch from '@/utils/tryCatch'

import FlightDetailsStep1And2 from './components/FlightDetailsStep1And2'
import FlightDetailsStep3, {type Step3Control} from './components/FlightDetailsStep3'
import FlightSummary from './components/FlightSummary'
import style from './FlightDetails.module.scss'
import useFlightSummary from './hooks/useFightSummary'

const emailFromPassengersAndContact = atom(get => {
  return get(passengersAndContactAtom).contact.email
})

const FlightDetails = memo(function FlightDetails() {
  const [locale] = useLocale()
  const latestLocale = useLatest(locale)
  const isAuthenticated = useIsAuthenticatedValue()
  const latestIsAuthenticated = useLatest(isAuthenticated)
  const stripe = useStripe()
  const elements = useElements()

  const router = useRouter()
  const store = useStore()
  const {applyRewards, coupon} = FlightDetailsRoute.useSearch()
  const latestCoupon = useLatest(coupon)
  const latestApplyRewards = useLatest(applyRewards)
  const [option, setOption] = useSearch(FlightDetailsRoute.id, 'option')
  const latestOption = useLatest(option)
  const [step, setStep] = useSearch(FlightDetailsRoute.id, 'step', {
    resetScroll: true,
    replace: false,
  })
  const latestStep = useLatest(step)
  const {flightId} = FlightDetailsRoute.useParams()

  // const askRef = useRef<AskInnoSearchControl>(null)
  const step3Ref = useRef<Step3Control>(null)
  const {total, flightDetails, isLoading} = useFlightSummary(flightId, option, applyRewards, coupon)
  const latestFlightDetails = useLatest(flightDetails)
  const step3Id = useId()
  const step4Id = useId()
  const step5Id = useId()

  // -------------------------------------------------------------------------------------------------------------------

  ;(function correctSelectedOption() {
    if (flightDetails && option >= flightDetails.booking_options.length) {
      void setOption(0)
    }
  })()

  // -------------------------------------------------------------------------------------------------------------------

  const [isSubmitting, setIsSubmitting] = useState(false)
  const contactInfoRef = useRef<ContactInfoControl>(null)

  const handleSubmit = useCallback(
    async (shouldShiftFocus = false) => {
      console.log('subbbbmitttt')
      switch (latestStep.current) {
        case 0:
        case 1:
        case 2:
          // No validation needed
          void setStep(3).then(() => {
            if (!shouldShiftFocus) return
            document.getElementById(step3Id)?.focus()
          })
          break
        case 3:
          try {
            const passengersAndContact = await step3Ref.current?.submit()
            if (passengersAndContact)
              void setStep(4).then(() => {
                if (!shouldShiftFocus) return
                document.getElementById(step4Id)?.focus()
              })
          } catch {
            // do nothing
          }
          break
        case 4:
          try {
            const contact = await contactInfoRef.current?.submit()
            if (contact)
              void setStep(5).then(() => {
                if (!shouldShiftFocus) return
                document.getElementById(step5Id)?.focus()
              })
          } catch {
            // do nothing
          }
          break
        case 5:
          try {
            if (!stripe || !elements) {
              return
            }

            const {error: submitError} = await elements.submit()
            if (submitError) {
              const resolveTo = RESOLVED_ERRORS.find(error =>
                [submitError.code, submitError.decline_code].includes(error.code),
              )

              if (resolveTo?.belongsTo === StripeElement.Address) {
                // do nothing, we don't use AddressElement here
              } else if (resolveTo?.belongsTo === StripeElement.Payment) {
                // do nothing, we are already at the right step
              } else {
                console.error('Stripe Error', submitError)
                // move back to the first step
                toastErrorMessage(
                  'Unexpected error, please start over. Sorry for the inconvenience!',
                )
                void setStep(0)
              }

              return
            }

            const {error: confirmationError, confirmationToken} =
              await stripe.createConfirmationToken({
                elements,
                params: {
                  payment_method_data: {
                    allow_redisplay: 'always',
                  },
                },
              })

            if (confirmationError) {
              console.error(confirmationError)
              toastErrorMessage(confirmationError)
              return
            }

            const assistance = store.get(assistanceInformationAtom)
            const passengersAndContact = store.get(passengersAndContactAtom)
            const contact = store.get(contactAtom)

            setIsSubmitting(true)

            const returnHref = router.buildLocation({
              to: FlightCheckoutRoute.fullPath,
              search: {
                guest: !latestIsAuthenticated.current ? true : undefined,
              },
            }).href

            invariant(latestFlightDetails.current)

            const finalBookingId =
              latestFlightDetails.current.booking_options[latestOption.current]?.flight_id

            invariant(finalBookingId)

            const {clientSecret, booking, paymentIntentStatus} = await bookFlight({
              email: contact.email,
              confirmationToken: confirmationToken.id,
              coupon: latestCoupon.current,
              isAuthenticated: latestIsAuthenticated.current,
              data: passengersAndContact,
              flightId: finalBookingId,
              optionIdx: latestOption.current,
              returnHref,
              locale: latestLocale.current,
              assistance,
              applyRewards: latestApplyRewards.current,
              roundUp: getRoundUp(store),
            })

            console.log({clientSecret, booking, paymentIntentStatus})

            if (clientSecret !== CLIENT_SECRET_FOR_0_AMOUNT_PAYMENT) {
              if (paymentIntentStatus === 'requires_action') {
                const {error: nextActionError} = await stripe.handleNextAction({clientSecret})

                if (nextActionError) {
                  // eslint-disable-next-line @typescript-eslint/only-throw-error -- its okay
                  throw nextActionError
                }

                await confirmBooking({
                  bookingId: booking.id,
                })
              }
            }

            const [updatedBooking, errorBooking] = await tryCatch(
              getBooking({
                bookingId: booking.id,
              }),
            )

            if (errorBooking) {
              throw new UnexpectedError()
            }

            if (updatedBooking.status === BookingStatus.AWAITING_PAYMENT) {
              await pRetry(
                () =>
                  confirmBooking({
                    bookingId: booking.id,
                  }),
                {retries: 3},
              )
            }

            await router.navigate({
              to: FlightCheckoutRoute.fullPath,
              search: {
                guest: !latestIsAuthenticated.current ? true : undefined,
                bookingId: booking.id,
              },
            })
          } catch (e) {
            console.error(e)
            toastErrorMessage(e)
            return
          } finally {
            setIsSubmitting(false)
          }
          break
      }
    },
    [setStep, step3Id, step4Id, step5Id, stripe, elements, store, router],
  )

  const submitFlightBooking = useCallback(async () => {
    return await step3Ref.current?.submit()
  }, [step3Ref])

  const emailFromPassengerAndContact = useAtomValue(emailFromPassengersAndContact)

  const initialContact = useMemo(() => {
    return {
      email: emailFromPassengerAndContact,
    }
  }, [emailFromPassengerAndContact])

  return (
    <div>
      <div className={style.Page}>
        <HeadTags title='Flight Details' />
        <SkipLink title='Skip to main content' to='#main-content' />
        <Navbar variant='dark' haveCart={false} />
        <main {...skipTargetProps('main-content')}>
          {(() => {
            if (isLoading) return <Loading label='Loading flight details...' />

            if (!flightDetails) return null

            return (
              <>
                <div className={style.BreadcrumbsContainer} role='region' aria-label='breadcrumb'>
                  <Breadcrumbs className={style.Breadcrumbs}>
                    <Breadcrumb className={style.Breadcrumb}>
                      <RACLink
                        className={style.Link}
                        to={FlightRoute.fullPath}
                        search={FLIGHT_ROUTE_DEFAULT_SEARCH}
                        activeOptions={{
                          exact: true,
                        }}
                      >
                        Flight
                      </RACLink>
                    </Breadcrumb>
                    <Breadcrumb className={style.Breadcrumb}>
                      <RACLink
                        className={style.Link}
                        to={FlightRoute.fullPath}
                        search={{
                          type: parseBookingType(flightDetails.metadata.type),
                          class: parseTravelClass(flightDetails.metadata.travel_class),
                          pax: {
                            adult: flightDetails.metadata.adults,
                            children: flightDetails.metadata.children,
                            infantsInSeat: flightDetails.metadata.infants_in_seat,
                            infantsOnLap: flightDetails.metadata.infants_on_lap,
                          },
                          departureAirport: flightDetails.metadata.departure_id,
                          arrivalAirport: flightDetails.metadata.arrival_id,
                          departureDate: flightDetails.metadata.outbound_date,
                          arrivalDate: flightDetails.metadata.return_date,
                          token: undefined,
                        }}
                        activeOptions={{
                          exact: true,
                        }}
                      >
                        Result
                      </RACLink>
                    </Breadcrumb>
                    <Breadcrumb className={style.Breadcrumb}>
                      <RACLink
                        className={style.Link}
                        to={FlightDetailsRoute.fullPath}
                        search={prevSearch => ({...prevSearch, step: 1})}
                        params={{flightId}}
                        activeOptions={{
                          exact: true,
                          includeSearch: true,
                        }}
                      >
                        Step 1 & 2
                      </RACLink>
                    </Breadcrumb>
                    {step >= 3 && (
                      <Breadcrumb className={style.Breadcrumb}>
                        <RACLink
                          className={style.Link}
                          to={FlightDetailsRoute.fullPath}
                          search={prevSearch => ({...prevSearch, step: 3})}
                          params={{flightId}}
                          activeOptions={{
                            exact: true,
                            includeSearch: true,
                          }}
                        >
                          Step 3
                        </RACLink>
                      </Breadcrumb>
                    )}
                    {step >= 4 && (
                      <Breadcrumb className={style.Breadcrumb}>
                        <RACLink
                          className={style.Link}
                          to={FlightDetailsRoute.fullPath}
                          search={prevSearch => ({...prevSearch, step: 4})}
                          params={{flightId}}
                          activeOptions={{
                            exact: true,
                            includeSearch: true,
                          }}
                        >
                          Step 4
                        </RACLink>
                      </Breadcrumb>
                    )}
                    {step === 5 && (
                      <Breadcrumb className={style.Breadcrumb}>
                        <RACLink
                          className={style.Link}
                          to={FlightDetailsRoute.fullPath}
                          search={prevSearch => ({...prevSearch, step: 5})}
                          params={{flightId}}
                          activeOptions={{
                            exact: true,
                            includeSearch: true,
                          }}
                        >
                          Step 5
                        </RACLink>
                      </Breadcrumb>
                    )}
                  </Breadcrumbs>
                </div>
                <div className={style.FlightDetails}>
                  <VisuallyHidden as='h1'>Flight details</VisuallyHidden>
                  <div className={style.FlightDetailsContent}>
                    {(() => {
                      if ([0, 1, 2].includes(step)) return <FlightDetailsStep1And2 />
                      if (step === 3)
                        return (
                          <div className={style.Step}>
                            <div
                              className={style.StepTitle}
                              role='heading'
                              aria-level={2}
                              id={step3Id}
                              tabIndex={-1}
                            >
                              Step 3: Add passenger information
                            </div>
                            <FlightDetailsStep3 ref={step3Ref} onSubmit={handleSubmit} />
                          </div>
                        )

                      if (step === 4)
                        return (
                          <>
                            <div className={style.Step}>
                              <div
                                className={style.StepTitle}
                                role='heading'
                                aria-level={2}
                                id={step4Id}
                                tabIndex={-1}
                              >
                                Step 4: Contact information
                              </div>
                            </div>
                            <div className={style.Form}>
                              <ContactInfo ref={contactInfoRef} initialContact={initialContact} />
                            </div>
                          </>
                        )

                      if (step === 5)
                        return (
                          <>
                            <div className={style.Step}>
                              <div
                                className={style.StepTitle}
                                role='heading'
                                aria-level={2}
                                id={step5Id}
                                tabIndex={-1}
                              >
                                Step 5: Payment method
                              </div>
                            </div>
                            <div className={style.Form}>
                              <div className={style.Wrapper}>
                                <PaymentMethod total={total} />
                              </div>
                            </div>
                          </>
                        )
                    })()}
                    <VisuallyHidden
                      as={PrimaryButton}
                      className={style.ContinueButtonHidden}
                      onPress={() => {
                        void handleSubmit(true)
                      }}
                    >
                      {(() => {
                        if (step < 5) return 'Next step'
                        return 'Pay now'
                      })()}
                    </VisuallyHidden>
                  </div>
                  <FlightSummary
                    isSubmitting={isSubmitting}
                    onSubmit={handleSubmit}
                    submitFlightBooking={submitFlightBooking}
                  />
                </div>
              </>
            )
          })()}
          {/* {flightDetails && flightDetails.metadata.thread_id && (
            <AskInnoSearch
              threadId={flightDetails.metadata.thread_id}
              prompts={flightDetails.suggested_prompts}
              ref={askRef}
            />
          )} */}
        </main>
      </div>
    </div>
  )
})

const FlightDetailsWrapped = memo(function FlightDetailsWrapped() {
  return (
    <PaymentProvider>
      <FlightDetails />
    </PaymentProvider>
  )
})

export default FlightDetailsWrapped
